import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Reply as ReplyIcon,
  Edit as EditIcon,
  UploadFile as UploadFileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import CaseForm from '../../components/CaseForm/CaseForm';
import TagWindow from '../../components/TagWindow/TagWindow';
import ReplyModal from '../../components/ReplyModal/ReplyModal';
// import WarningPopup from '../../components/Popup/Warning/Warning';

import { deleteCase, getCases } from '../../../http/caseAPI';
import { TABLE_THEME, USER_ROLES } from '../../../util/constants/AppConstants';
import { compareDates, formatUnixDate } from '../../../util/HelperFunctions';
import { setNotify } from '../../../datastore/actions/actionActions';

import './CaseTable.scss';

const CaseTable = () => {
  const dispatch = useDispatch();

  const config = useSelector((state) => state.config.config);

  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [isCaseFormOpen, setIsCaseFormOpen] = useState(false);
  const [activeCase, setActiveCase] = useState(null);
  const [isTagWindowOpen, setIsTagWindowOpen] = useState(false);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  // const [deleteRequest, setDeleteRequest] = useState(false);
  // const [deleteId, setDeleteId] = useState(null);

  const { userInfo } = useOutletContext();

  const mppRoleValue = USER_ROLES.find((role) => role.short === 'MPP').value;
  const councilorRoleValue = USER_ROLES.find((role) => role.short === 'Councillor').value;

  const isMpp = userInfo && userInfo.role === mppRoleValue;
  const isCouncillor = userInfo && userInfo.role === councilorRoleValue;

  const handleGetCases = async (userId) => {
    setLoading(true);

    const res = await getCases(config, userId);

    if (res.status === 200) {
      setCases(res.data.cases);
      setLoading(false);
    } else {
      console.log('ERROR: Grabbing Cases');
    }
  };

  useEffect(() => {
    if (config.api && userInfo) {
      handleGetCases(userInfo.id);
    }
  }, [config, userInfo]);

  const handleCreateNew = () => {
    setIsCaseFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsCaseFormOpen(false);
    setActiveCase(null);
  };

  const handleOpenTagWindow = (activeCase) => {
    setIsTagWindowOpen(true);
    setActiveCase(activeCase);
  };

  const handleCloseTagWindow = () => {
    setIsTagWindowOpen(false);
    setActiveCase(null);
  };

  const handleReplyMessage = (e, id) => {
    e.stopPropagation();

    const caseData = cases.find((elem) => elem.id === id);

    if (!caseData.email) {
      dispatch(
        setNotify({
          status: true,
          type: '',
          title: 'Attention',
          message: 'Cannot send reply: constituent has no email',
        }),
      );
    } else {
      setIsReplyModalOpen(true);
      setActiveCase(caseData);
    }
  };

  const handleEditCase = (e, id) => {
    e.stopPropagation();

    setIsCaseFormOpen(true);
    const caseData = cases.find((elem) => elem.id === id);
    setActiveCase(caseData);
  };

  // const handleDeleteCase = (e, id) => {
  //   e.stopPropagation();

  //   setDeleteRequest(true);
  //   setDeleteId(id);
  // };

  // const handleCaseDelete = async () => {
  //   const res = await deleteCase(config, deleteId, userInfo.id);

  //   if (res.status === 200) {
  //     dispatch(
  //       setNotify({
  //         status: true,
  //         type: 'success',
  //         title: 'Success',
  //         message: res.data.message,
  //       }),
  //     );
  //   } else {
  //     dispatch(
  //       setNotify({
  //         status: true,
  //         type: 'error',
  //         title: 'Error',
  //         message: res.data.message,
  //       }),
  //     );
  //   }

  //   setDeleteRequest(false);
  //   setDeleteId(null);
  //   handleGetCases(userInfo.id);
  // };

  const highlightMatch = (text, query) => {
    if (!query) return text;

    const regex = new RegExp(`(${query})`, 'ig');
    const parts = text.split(regex);

    return parts.map((part, i) =>
      regex.test(part) ? (
        <span key={i} style={{ backgroundColor: '#ffcb7f' }}>
          {part}
        </span>
      ) : (
        part
      ),
    );
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorKey: 'case_id',
        header: 'Case ID',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'email',
        header: 'Email',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'phone',
        header: 'Phone',
        minSize: 110,
        grow: 1,
        enableSorting: false,
      },
      {
        accessorKey: 'tags',
        header: 'Tags',
        minSize: 180,
        grow: 1,
        enableSorting: false,
        Cell: ({ cell, column, row }) => {
          const tags = cell.getValue();
          const filterValue = column.getFilterValue() || '';

          return (
            <div className="tags">
              {tags.map((tag, i) => (
                <span key={i} className="tag">
                  {highlightMatch(tag, filterValue)}
                </span>
              ))}
              <span
                className="tag tag-add"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenTagWindow(row.original);
                }}
              >
                +
              </span>
            </div>
          );
        },
        filterFn: (row, columnId, filterValue) => {
          const rowTags = row.getValue(columnId);
          if (!rowTags || !rowTags.length) return false;

          return rowTags.some((tag) => tag.toLowerCase().includes(String(filterValue).toLowerCase()));
        },
      },
      {
        accessorKey: 'latest_message_timestamp_utc',
        header: 'Latest email date',
        filterVariant: 'date',
        Cell: ({ cell }) => formatUnixDate(cell.getValue(), false),
        filterFn: (row, columnId, filterValue) => compareDates(new Date(row.getValue(columnId) * 1000), filterValue.$d),
        minSize: 200,
        grow: 1,
      },
    ];
    return baseColumns;
  }, []);

  const theme = createTheme(TABLE_THEME);

  const memoColumns = useMemo(() => columns, [columns]);
  const memoData = useMemo(() => cases, [cases]);

  const table = useMaterialReactTable({
    columns: memoColumns,
    data: memoData,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnResizing: true,
    enableColumnFilters: true,
    enableFacetedValues: true,
    getRowId: (originalRow) => originalRow.id,
    renderTopToolbarCustomActions: () => (
      <div className="table__toolbar-wrapper">
        <div className="table__toolbar-btn create-btn" onClick={handleCreateNew}>
          Create New +
        </div>
      </div>
    ),
    state: { isLoading: loading },
    positionToolbarAlertBanner: 'none',
    muiPaginationProps: {
      color: 'primary',
      shape: 'rounded',
      showRowsPerPage: false,
      showFirstButton: true,
      showLastButton: true,
    },
    paginationDisplayMode: 'pages',
    initialState: {
      columnOrder: ['case_id', 'name', 'email', 'phone', 'tags', 'latest_message_timestamp_utc'],
      pagination: { pageSize: 10, pageIndex: 0 },
      showColumnFilters: true,
    },
    muiTableBodyCellProps: {
      sx: {
        marginRight: '12px',
        color: '#343434',
        fontSize: '14px',
        fontWeight: 'normal',
        border: 'none',
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        marginRight: '12px',
        color: '#343434',
        fontSize: '16px',
        fontWeight: 500,
        border: 'none',
      },
    },
    muiTableHeadRowProps: {
      sx: {
        borderTop: '1px solid #D3D3D3',
        borderBottom: '1px solid #D3D3D3',
      },
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => window.open(`/case/${row.original.id}`, '_blank'),
      sx: {
        cursor: 'pointer',
        borderBottom: '1px solid #D3D3D3',
        backgroundColor: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#F7F7F7',
        },
      },
    }),
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', flexWrap: 'nowrap' }}>
        <Tooltip title="Reply">
          <IconButton
            className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
            onClick={(e) => {
              handleReplyMessage(e, row.original.id);
            }}
          >
            <ReplyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton
            className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
            onClick={(e) => {
              handleEditCase(e, row.original.id);
            }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Upload files">
          <IconButton className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`} onClick={() => {}}>
            <UploadFileIcon />
          </IconButton>
        </Tooltip>
        {/* <Tooltip title="Delete">
          <IconButton
            className={`icon-button ${isMpp || isCouncillor ? 'blue' : ''}`}
            onClick={(e) => {
              handleDeleteCase(e, row.original.id);
            }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip> */}
      </Box>
    ),
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 140,
        grow: false,
        muiTableHeadCellProps: {
          sx: {
            fontSize: '16px',
            fontWeight: 500,
            position: 'sticky',
            right: 0,
            zIndex: 10,
            border: 'none',
            boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1) !important',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            position: 'sticky',
            right: 0,
            zIndex: 10,
            border: 'none',
            boxShadow: '-4px 0 8px -2px rgba(0, 0, 0, 0.1) !important',
          },
        },
      },
    },
  });

  return (
    <div className="cases">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <ThemeProvider theme={theme}>
          <div className="table">
            <MaterialReactTable table={table} />
          </div>
          {isCaseFormOpen && (
            <CaseForm
              isOpen={isCaseFormOpen}
              onCloseHandler={handleCloseForm}
              caseData={activeCase}
              handleGetCaseData={() => handleGetCases(userInfo.id)}
            />
          )}
          {isTagWindowOpen && (
            <TagWindow
              isOpen={isTagWindowOpen}
              onCloseHandler={handleCloseTagWindow}
              activeCase={activeCase}
              handleGetCases={() => handleGetCases(userInfo.id)}
            />
          )}
          {isReplyModalOpen && (
            <ReplyModal
              isOpen={isReplyModalOpen}
              setIsOpen={setIsReplyModalOpen}
              caseId={activeCase.case_id}
              recipientEmail={activeCase.email.split(';')[0]}
              handleGetFeedbackData={() => {}}
            />
          )}
          {/* {deleteRequest && (
            <WarningPopup
              isOpen={deleteRequest}
              onSubmitHandler={handleCaseDelete}
              onCancelHandler={() => setDeleteRequest(false)}
            />
          )} */}
        </ThemeProvider>
      </LocalizationProvider>
    </div>
  );
};

export default CaseTable;
